import {
  PAYMENT_METHOD_STARS,
  PAYMENT_METHOD_COBO,
  isTelegramStarsEnabled,
  getStarsPerUsd,
  estimateStarsFromUsd,
  createTelegramStarsInvoice,
  openTelegramStarsInvoice,
  buildPendingStarsPayment,
} from '@/utils/telegramStarsPayment'
import {
  AIX_WAY_CODES,
  buildPendingAixPayment,
  createAixPayment,
  getAixPaymentMethodLabelKey,
  getAixWayCode,
  isAixPaymentMethod,
  isSameAixPaymentMethod,
  loadAixPayConfig,
  shouldOfferAixPaymentMethods,
  toAixPaymentMethod,
} from '@/utils/aixpayPayment'
import {
  isTelegramWebAppActive,
  getTelegramWebApp,
  showNativeConfirm,
  showNativePopup,
} from '@/utils/telegramWebAppClient'
import { showAppConfirm } from '@/utils/telegramDialog'
import { setPendingPayment } from '@/utils/pendingPaymentStorage'
import { resolveApiErrorMessage } from '@/utils/apiResponse'
import { openExternalLink, openPaymentLinkInSystemBrowser } from '@/utils/telegramWebAppClient'

export { PAYMENT_METHOD_STARS, PAYMENT_METHOD_COBO, estimateStarsFromUsd, getStarsPerUsd }
export { toAixPaymentMethod, isAixPaymentMethod, getAixWayCode, getAixPaymentMethodLabelKey, isSameAixPaymentMethod }

let cachedPaymentMethods = null

/** 刷新可选支付方式（Stars / AIX RUB / Cobo） */
export async function refreshAvailablePaymentMethods(api, options = {}) {
  const methods = []
  const offerAix = shouldOfferAixPaymentMethods(options)

  if (isTelegramWebAppActive() && isTelegramStarsEnabled()) {
    methods.push(PAYMENT_METHOD_STARS)
  }

  if (offerAix) {
    const config = api ? await loadAixPayConfig(api) : { wayCodes: AIX_WAY_CODES }
    const wayCodes = config.wayCodes?.length ? config.wayCodes : AIX_WAY_CODES
    for (const wayCode of wayCodes) {
      methods.push(toAixPaymentMethod(wayCode))
    }
  }

  methods.push(PAYMENT_METHOD_COBO)

  cachedPaymentMethods = methods
  return cachedPaymentMethods
}

export function getAvailablePaymentMethods() {
  return cachedPaymentMethods || [PAYMENT_METHOD_COBO]
}

export function shouldShowPaymentMethodPicker(methods = getAvailablePaymentMethods()) {
  if (methods.some((method) => isAixPaymentMethod(method))) {
    return methods.length > 0
  }
  return methods.length > 1
}

export function getDefaultPaymentMethod() {
  const methods = getAvailablePaymentMethods()
  return methods[0] || PAYMENT_METHOD_COBO
}

export function getPaymentMethodTitle(method, t) {
  if (method === PAYMENT_METHOD_STARS) return t('Payment_Method_Stars')
  if (method === PAYMENT_METHOD_COBO) return t('Payment_Method_Cobo')
  if (isAixPaymentMethod(method)) return t(getAixPaymentMethodLabelKey(method))
  return String(method || '')
}

export function getPaymentMethodDesc(method, t, { starsAmount } = {}) {
  if (method === PAYMENT_METHOD_STARS) {
    return t('Payment_Method_Stars_Desc', { stars: starsAmount || '—' })
  }
  if (method === PAYMENT_METHOD_COBO) return t('Payment_Method_Cobo_Desc')
  if (isAixPaymentMethod(method)) return t('Payment_Method_Aix_Desc')
  return ''
}

export function buildPendingCoboPayment({
  orderNo,
  paymentLink,
  amountUsd,
  productName,
  expiredInMinutes = 30,
}) {
  return {
    orderNo,
    paymentLink,
    amountUsd,
    productName,
    paymentMethod: PAYMENT_METHOD_COBO,
    expiredInMinutes,
    createdAt: Date.now(),
  }
}

async function payWithStars({
  api,
  router,
  orderNo,
  productName,
  starsPay,
}) {
  await setPendingPayment(buildPendingStarsPayment({
    orderNo,
    invoiceLink: starsPay.invoiceLink,
    starsAmount: starsPay.starsAmount,
    amountUsd: starsPay.amountUsd,
    productName,
    expiredInMinutes: starsPay.expiredInMinutes,
  }))

  await router.push('/payment-status')
  return openTelegramStarsInvoice(starsPay.invoiceLink)
}

async function promptBeforeExternalPayment(t, { useSystemBrowser = false } = {}) {
  if (!t) return true

  const title = useSystemBrowser
    ? t('Payment_External_System_Browser_Title')
    : t('Payment_External_Return_Title')
  const message = useSystemBrowser
    ? t('Payment_External_System_Browser_Hint')
    : t('Payment_External_Return_Hint')
  const tg = getTelegramWebApp()

  if (isTelegramWebAppActive() && typeof tg?.showPopup === 'function') {
    const buttonId = await showNativePopup({
      title,
      message,
      buttons: [
        { id: 'proceed', type: 'default', text: t('Payment_Go_Pay') },
        { id: 'cancel', type: 'cancel', text: t('Payment_Cancel') },
      ],
    })
    if (buttonId === 'proceed') return true
    if (buttonId === 'cancel') return false
  }

  if (isTelegramWebAppActive() && typeof tg?.showConfirm === 'function') {
    return showNativeConfirm(`${title}\n\n${message}`)
  }

  return showAppConfirm(message, title, {
    confirmButtonText: t('Payment_Go_Pay'),
    cancelButtonText: t('Payment_Cancel'),
    type: 'info',
    closeOnClickModal: false,
  })
}

async function completeExternalPaymentRedirect({
  t,
  router,
  paymentLink,
  useSystemBrowser = false,
  openLink = useSystemBrowser ? openPaymentLinkInSystemBrowser : openExternalLink,
}) {
  const shouldOpen = await promptBeforeExternalPayment(t, { useSystemBrowser })
  if (!shouldOpen) return 'cancelled'

  await router.push('/payment-status')

  return new Promise((resolve) => {
    setTimeout(() => {
      openLink(paymentLink)
      resolve('opened')
    }, 300)
  })
}

async function payWithCobo({
  api,
  router,
  orderNo,
  productName,
  paymentLink,
  amountUsd,
  expiredInMinutes,
  t,
}) {
  await setPendingPayment(buildPendingCoboPayment({
    orderNo,
    paymentLink,
    amountUsd,
    productName,
    expiredInMinutes,
  }))

  return completeExternalPaymentRedirect({ t, router, paymentLink })
}

async function payWithAix({
  api,
  router,
  orderNo,
  productName,
  paymentMethod,
  existingPaymentUrl = null,
  t,
}) {
  const wayCode = getAixWayCode(paymentMethod)
  let paymentLink = existingPaymentUrl
  let amountRub
  let expiredInMinutes = 30

  if (!paymentLink) {
    const payResult = await createAixPayment(api, orderNo, wayCode)
    if (!payResult.ok) {
      return payResult
    }
    paymentLink = payResult.paymentLink
    amountRub = payResult.amountRub
    expiredInMinutes = payResult.expiredInMinutes
  }

  await setPendingPayment(buildPendingAixPayment({
    orderNo,
    paymentLink,
    amountRub,
    productName,
    wayCode,
    expiredInMinutes,
  }))

  const result = await completeExternalPaymentRedirect({
    t,
    router,
    paymentLink,
    useSystemBrowser: true,
  })
  if (result === 'cancelled') {
    return { ok: true, opened: false }
  }
  return { ok: true, opened: true }
}

/**
 * 按用户所选方式发起支付
 */
export async function executeOrderPayment({
  api,
  router,
  orderNo,
  productName,
  amountUsd,
  paymentMethod,
  existingCoboPaymentUrl = null,
  existingAixPaymentUrl = null,
  t,
}) {
  if (paymentMethod === PAYMENT_METHOD_STARS) {
    const starsPay = await createTelegramStarsInvoice(api, orderNo)
    if (!starsPay.ok) {
      return { ok: false, message: starsPay.message, method: PAYMENT_METHOD_STARS }
    }
    const invoiceStatus = await payWithStars({
      api,
      router,
      orderNo,
      productName,
      starsPay,
    })
    return { ok: true, method: PAYMENT_METHOD_STARS, invoiceStatus }
  }

  if (isAixPaymentMethod(paymentMethod)) {
    const aixResult = await payWithAix({
      api,
      router,
      orderNo,
      productName,
      paymentMethod,
      existingPaymentUrl: existingAixPaymentUrl,
      t,
    })
    if (!aixResult.ok) {
      return {
        ok: false,
        message: aixResult.message,
        method: paymentMethod,
      }
    }
    return { ok: true, method: paymentMethod }
  }

  let paymentLink = existingCoboPaymentUrl
  let expiredInMinutes = 30
  let resolvedAmountUsd = amountUsd

  if (!paymentLink) {
    const payRes = await api.payment.createPaymentOrder({ order_no: orderNo })
    paymentLink = payRes?.data?.data?.payment_link
    if (!paymentLink || payRes?.data?.status !== '0') {
      return {
        ok: false,
        message: resolveApiErrorMessage(payRes?.data),
        method: PAYMENT_METHOD_COBO,
      }
    }
    expiredInMinutes = payRes?.data?.data?.expired_in_minutes ?? 30
    resolvedAmountUsd = payRes?.data?.data?.amount_usd ?? amountUsd
  }

  await payWithCobo({
    api,
    router,
    orderNo,
    productName,
    paymentLink,
    amountUsd: resolvedAmountUsd,
    expiredInMinutes,
    t,
  })
  return { ok: true, method: PAYMENT_METHOD_COBO }
}

async function pickAixPaymentMethod({ t, methods }) {
  const aixMethods = methods.filter((method) => isAixPaymentMethod(method))
  if (!aixMethods.length) return null
  if (aixMethods.length === 1) return aixMethods[0]

  const buttonId = await showNativePopup({
    title: t('Payment_Method_Aix_Group'),
    message: t('Payment_Method_Pick_Message'),
    buttons: [
      ...aixMethods.map((method) => ({
        id: method,
        type: 'default',
        text: t(getAixPaymentMethodLabelKey(method)),
      })),
      { id: 'cancel', type: 'cancel', text: t('Payment_Cancel') },
    ],
  })
  return aixMethods.includes(buttonId) ? buttonId : null
}

export async function pickPaymentMethod({ t, api, locale, onPickRequest, preferInAppPicker = false }) {
  if (api) {
    await refreshAvailablePaymentMethods(api, { locale })
  }

  const methods = getAvailablePaymentMethods()
  if (methods.length <= 1) {
    return getDefaultPaymentMethod()
  }

  const pickInApp = typeof onPickRequest === 'function'
    ? () => onPickRequest({ methods, t })
    : null

  if (pickInApp && preferInAppPicker) {
    return pickInApp()
  }

  if (isTelegramWebAppActive()) {
    const hasAix = methods.some((method) => isAixPaymentMethod(method))
    const buttons = []

    if (methods.includes(PAYMENT_METHOD_STARS)) {
      buttons.push({ id: PAYMENT_METHOD_STARS, type: 'default', text: t('Payment_Method_Stars') })
    }
    if (hasAix) {
      buttons.push({ id: '__aix_group__', type: 'default', text: t('Payment_Method_Aix_Group') })
    }
    if (methods.includes(PAYMENT_METHOD_COBO)) {
      buttons.push({ id: PAYMENT_METHOD_COBO, type: 'default', text: t('Payment_Method_Cobo') })
    }
    buttons.push({ id: 'cancel', type: 'cancel', text: t('Payment_Cancel') })

    const buttonId = await showNativePopup({
      title: t('Payment_Method_Label'),
      message: t('Payment_Method_Pick_Message'),
      buttons,
    })

    if (buttonId === PAYMENT_METHOD_STARS || buttonId === PAYMENT_METHOD_COBO) {
      return buttonId
    }
    if (buttonId === '__aix_group__') {
      return pickAixPaymentMethod({ t, methods })
    }
    return null
  }

  if (pickInApp) {
    return pickInApp()
  }

  return getDefaultPaymentMethod()
}
